import React, { useContext, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import "../css/checkout.css";

const commerceConfig = siteConfig.commerce || {};
const shippingConfig = siteConfig.shipping || commerceConfig.shipping || {};

const toPositiveNumber = (value, fallback) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? parsedValue
    : fallback;
};

const SHIPPING_RATE = toPositiveNumber(
  shippingConfig.usRate ??
    shippingConfig.us?.rate ??
    shippingConfig.cost ??
    commerceConfig.shippingCost,
  15
);
const FREE_SHIPPING_THRESHOLD = toPositiveNumber(
  shippingConfig.freeShippingThreshold ??
    shippingConfig.freeThreshold ??
    commerceConfig.freeShippingThreshold,
  250
);
const PROMO_CODE = String(
  commerceConfig.discountCode || commerceConfig.promoCode || "CHANGE10"
).toUpperCase();
const configuredDiscount = toPositiveNumber(
  commerceConfig.discountRate ?? commerceConfig.discounts?.[PROMO_CODE],
  0.1
);
const PROMO_RATE = configuredDiscount > 1
  ? configuredDiscount / 100
  : configuredDiscount;

const currencyFormatter = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency?.code || "USD",
});

const formatMoney = (value) => currencyFormatter.format(value);

const formatCardNumber = (input) =>
  input
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

const formatExpiry = (input) => {
  const digits = input.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
};

const isValidExpiry = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 4) return false;

  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  if (month < 1 || month > 12) return false;

  const now = new Date();
  const expiryMonthEnd = new Date(year, month, 0, 23, 59, 59);
  return expiryMonthEnd >= now;
};

const isValidCardNumber = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length < 15 || digits.length > 16) return false;

  const checksum = digits
    .split("")
    .reverse()
    .reduce((total, digit, index) => {
      let nextDigit = Number(digit);
      if (index % 2 === 1) {
        nextDigit *= 2;
        if (nextDigit > 9) nextDigit -= 9;
      }
      return total + nextDigit;
    }, 0);

  return checksum % 10 === 0;
};

const initialForm = {
  contact: "",
  country: "US",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  address2: "",
  city: "",
  region: "",
  postalCode: "",
  phone: "",
  shippingMethod: "standard",
  cardNumber: "",
  expiry: "",
  cvv: "",
  cardName: "",
  acceptTerms: false,
};

const validateCheckout = (values) => {
  const nextErrors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const postalPattern = /^\d{5}(?:-\d{4})?$/;

  if (!values.contact.trim()) {
    nextErrors.contact = "Enter an email address.";
  } else if (!emailPattern.test(values.contact.trim())) {
    nextErrors.contact = "Enter a valid email address.";
  }
  if (!values.country) nextErrors.country = "Select a country or region.";
  if (!values.firstName.trim()) nextErrors.firstName = "Enter a first name.";
  if (!values.lastName.trim()) nextErrors.lastName = "Enter a last name.";
  if (!values.address.trim()) nextErrors.address = "Enter an address.";
  if (!values.city.trim()) nextErrors.city = "Enter a city.";
  if (!values.region.trim()) nextErrors.region = "Enter a state.";
  if (!values.postalCode.trim()) {
    nextErrors.postalCode = "Enter a ZIP code.";
  } else if (!postalPattern.test(values.postalCode.trim())) {
    nextErrors.postalCode = "Use a 5-digit or ZIP+4 format.";
  }
  if (!values.shippingMethod) {
    nextErrors.shippingMethod = "Select a shipping method.";
  }
  if (!isValidCardNumber(values.cardNumber)) {
    nextErrors.cardNumber = "Enter a valid card number.";
  }
  if (!isValidExpiry(values.expiry)) {
    nextErrors.expiry = "Enter a valid future expiration date.";
  }
  if (!/^\d{3,4}$/.test(values.cvv)) {
    nextErrors.cvv = "Enter the 3 or 4-digit security code.";
  }
  if (!values.cardName.trim()) {
    nextErrors.cardName = "Enter the name shown on your card.";
  }
  if (!values.acceptTerms) {
    nextErrors.acceptTerms = "Please accept the terms to continue.";
  }

  return nextErrors;
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const incomingDiscount =
    String(location.state?.discountCode || "").toUpperCase() === PROMO_CODE
      ? PROMO_CODE
      : "";

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discountInput, setDiscountInput] = useState(incomingDiscount);
  const [appliedDiscount, setAppliedDiscount] = useState(incomingDiscount);
  const [discountMessage, setDiscountMessage] = useState(
    incomingDiscount ? `${PROMO_CODE} is applied.` : ""
  );

  const resolvedItems = useMemo(
    () =>
      cartItems.reduce((items, cartItem) => {
        const product = productData.find(
          (candidate) => String(candidate.id) === String(cartItem.id)
        );
        if (!product) return items;

        const quantity = Math.max(1, Number(cartItem.quantity) || 1);
        return [...items, { ...cartItem, quantity, product }];
      }, []),
    [cartItems]
  );

  const subtotal = resolvedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const discount = appliedDiscount === PROMO_CODE ? subtotal * PROMO_RATE : 0;
  const discountedSubtotal = Math.max(0, subtotal - discount);
  const hasFreeShipping =
    FREE_SHIPPING_THRESHOLD === 0 ||
    discountedSubtotal >= FREE_SHIPPING_THRESHOLD;
  const shipping = hasFreeShipping ? 0 : SHIPPING_RATE;
  const total = discountedSubtotal + shipping;
  const itemCount = resolvedItems.reduce(
    (count, item) => count + item.quantity,
    0
  );

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const updateField = (name, value) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!current[name]) return current;
      const nextErrors = { ...current };
      delete nextErrors[name];
      return nextErrors;
    });
    setSubmitMessage("");
  };

  const handleFieldChange = (event) => {
    const { name, type, checked, value } = event.target;
    updateField(name, type === "checkbox" ? checked : value);
  };

  const handleDiscountSubmit = (event) => {
    event.preventDefault();
    const normalizedCode = discountInput.trim().toUpperCase();

    if (!normalizedCode) {
      setDiscountMessage("Enter a discount code.");
      return;
    }

    if (normalizedCode !== PROMO_CODE) {
      setAppliedDiscount("");
      setDiscountMessage(`This code is not valid. Try ${PROMO_CODE}.`);
      return;
    }

    setAppliedDiscount(PROMO_CODE);
    setDiscountInput(PROMO_CODE);
    setDiscountMessage(`${PROMO_CODE} applied — ${Math.round(PROMO_RATE * 100)}% off.`);
  };

  const removeDiscount = () => {
    setAppliedDiscount("");
    setDiscountInput("");
    setDiscountMessage("Discount removed.");
  };

  const handlePlaceOrder = async (event) => {
    event.preventDefault();
    if (isSubmitting || !resolvedItems.length) return;

    const nextErrors = validateCheckout(formData);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length) {
      setSubmitMessage("Please review the highlighted fields before paying.");
      const firstInvalidField = Object.keys(nextErrors)[0];
      if (typeof document !== "undefined") {
        document.getElementById(`checkout-${firstInvalidField}`)?.focus();
      }
      return;
    }

    setSubmitMessage("");
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 850));

    const orderNumber = `AA-${Date.now().toString().slice(-8)}`;
    clearCart();
    navigate("/paysuccess", {
      replace: true,
      state: { orderNumber, total },
    });
    scrollToTop();
  };

  const renderOrderSummary = (instance, showHeading = true) => (
    <div className="checkout-summary">
      {showHeading && <h2 id={`checkout-summary-${instance}`}>Order summary</h2>}

      <div className="checkout-summary__items">
        {resolvedItems.map((item) => {
          const { product } = item;
          const image = product.image || product.images?.[0];

          return (
            <article
              className="checkout-summary__item"
              key={`${instance}-${item.id}-${item.size || "one"}-${item.color || "default"}`}
            >
              <div className="checkout-summary__image">
                <img src={image} alt={product.name} decoding="async" />
                <span aria-label={`Quantity ${item.quantity}`}>
                  {item.quantity}
                </span>
              </div>
              <div className="checkout-summary__item-copy">
                <h3>{product.name}</h3>
                <p>
                  {item.color || product.colors?.[0] || "Selected"}
                  <span aria-hidden="true"> / </span>
                  {item.size || "One size"}
                </p>
              </div>
              <p className="checkout-summary__item-price">
                {formatMoney(Number(product.price) * item.quantity)}
              </p>
            </article>
          );
        })}
      </div>

      <form
        className="checkout-summary__discount"
        onSubmit={handleDiscountSubmit}
        noValidate
      >
        <label htmlFor={`checkout-discount-${instance}`}>Discount code</label>
        <div>
          <input
            id={`checkout-discount-${instance}`}
            value={discountInput}
            onChange={(event) => {
              setDiscountInput(event.target.value);
              setDiscountMessage("");
            }}
            placeholder={PROMO_CODE}
            autoComplete="off"
            disabled={Boolean(appliedDiscount)}
          />
          {appliedDiscount ? (
            <button type="button" onClick={removeDiscount}>
              Remove
            </button>
          ) : (
            <button type="submit">Apply</button>
          )}
        </div>
        <p className={appliedDiscount ? "is-success" : ""} aria-live="polite">
          {discountMessage ||
            `Use ${PROMO_CODE} for ${Math.round(PROMO_RATE * 100)}% off.`}
        </p>
      </form>

      <dl className="checkout-summary__totals">
        <div>
          <dt>Subtotal</dt>
          <dd>{formatMoney(subtotal)}</dd>
        </div>
        {discount > 0 && (
          <div className="checkout-summary__discount-row">
            <dt>Discount ({PROMO_CODE})</dt>
            <dd>−{formatMoney(discount)}</dd>
          </div>
        )}
        <div>
          <dt>Shipping</dt>
          <dd>{shipping === 0 ? "Free" : formatMoney(shipping)}</dd>
        </div>
        <div className="checkout-summary__total">
          <dt>
            Total <small>{siteConfig.currency?.code || "USD"}</small>
          </dt>
          <dd>{formatMoney(total)}</dd>
        </div>
      </dl>
      <p className="checkout-summary__note">
        Taxes included where applicable. Shipping to the United States.
      </p>
    </div>
  );

  if (!resolvedItems.length) {
    return (
      <main className="checkout-page checkout-page--empty" id="main-content">
        <section aria-labelledby="empty-checkout-heading">
          <p className="checkout-page__eyebrow">Checkout</p>
          <h1 id="empty-checkout-heading">Your bag is empty</h1>
          <p>Add a piece to your bag before continuing to checkout.</p>
          <Link to="/shop" onClick={scrollToTop}>
            Return to shop
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="checkout-page" id="main-content">
      <div className="checkout-page__shell">
        <div className="checkout-page__form-column">
          <div className="checkout-page__form-inner">
            <details className="checkout-page__mobile-summary">
              <summary>
                <span>Order summary ({itemCount})</span>
                <strong>{formatMoney(total)}</strong>
              </summary>
              {renderOrderSummary("mobile", false)}
            </details>

            <div className="checkout-page__intro">
              <Link
                className="checkout-page__back"
                to="/cartpage"
                onClick={scrollToTop}
              >
                <span aria-hidden="true">←</span> Return to bag
              </Link>
              <p className="checkout-page__brand">
                {siteConfig.logo?.wordmark || siteConfig.brandName}
              </p>
              <p className="checkout-page__eyebrow">Secure checkout</p>
              <h1>Checkout</h1>
              <p className="checkout-page__steps" aria-label="Checkout steps">
                Contact <span>→</span> Delivery <span>→</span> Shipping{" "}
                <span>→</span> Payment
              </p>
            </div>

            <form
              className="checkout-form"
              onSubmit={handlePlaceOrder}
              noValidate
            >
              <section className="checkout-form__section" aria-labelledby="contact-heading">
                <div className="checkout-form__section-heading">
                  <span>01</span>
                  <div>
                    <h2 id="contact-heading">Contact</h2>
                    <p>Your receipt and delivery updates go here.</p>
                  </div>
                </div>
                <div className="checkout-field">
                  <label htmlFor="checkout-contact">Email address</label>
                  <input
                    id="checkout-contact"
                    name="contact"
                    type="email"
                    value={formData.contact}
                    onChange={handleFieldChange}
                    autoComplete="email"
                    placeholder="name@example.com"
                    aria-invalid={Boolean(errors.contact)}
                    aria-describedby={errors.contact ? "checkout-contact-error" : undefined}
                  />
                  {errors.contact && (
                    <p className="checkout-field__error" id="checkout-contact-error">
                      {errors.contact}
                    </p>
                  )}
                </div>
              </section>

              <section className="checkout-form__section" aria-labelledby="delivery-heading">
                <div className="checkout-form__section-heading">
                  <span>02</span>
                  <div>
                    <h2 id="delivery-heading">Delivery</h2>
                    <p>Where should we send your order?</p>
                  </div>
                </div>

                <div className="checkout-field">
                  <label htmlFor="checkout-country">Country / region</label>
                  <select
                    id="checkout-country"
                    name="country"
                    value={formData.country}
                    onChange={handleFieldChange}
                    autoComplete="country"
                    aria-invalid={Boolean(errors.country)}
                  >
                    <option value="US">United States</option>
                  </select>
                  {errors.country && (
                    <p className="checkout-field__error">{errors.country}</p>
                  )}
                </div>

                <div className="checkout-form__row">
                  <div className="checkout-field">
                    <label htmlFor="checkout-firstName">First name</label>
                    <input
                      id="checkout-firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleFieldChange}
                      autoComplete="given-name"
                      aria-invalid={Boolean(errors.firstName)}
                    />
                    {errors.firstName && (
                      <p className="checkout-field__error">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="checkout-lastName">Last name</label>
                    <input
                      id="checkout-lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleFieldChange}
                      autoComplete="family-name"
                      aria-invalid={Boolean(errors.lastName)}
                    />
                    {errors.lastName && (
                      <p className="checkout-field__error">{errors.lastName}</p>
                    )}
                  </div>
                </div>

                <div className="checkout-field">
                  <label htmlFor="checkout-company">
                    Company <span>(optional)</span>
                  </label>
                  <input
                    id="checkout-company"
                    name="company"
                    value={formData.company}
                    onChange={handleFieldChange}
                    autoComplete="organization"
                  />
                </div>

                <div className="checkout-field">
                  <label htmlFor="checkout-address">Address</label>
                  <input
                    id="checkout-address"
                    name="address"
                    value={formData.address}
                    onChange={handleFieldChange}
                    autoComplete="address-line1"
                    placeholder="Street and house number"
                    aria-invalid={Boolean(errors.address)}
                  />
                  {errors.address && (
                    <p className="checkout-field__error">{errors.address}</p>
                  )}
                </div>

                <div className="checkout-field">
                  <label htmlFor="checkout-address2">
                    Apartment, suite, etc. <span>(optional)</span>
                  </label>
                  <input
                    id="checkout-address2"
                    name="address2"
                    value={formData.address2}
                    onChange={handleFieldChange}
                    autoComplete="address-line2"
                  />
                </div>

                <div className="checkout-form__row checkout-form__row--three">
                  <div className="checkout-field checkout-field--city">
                    <label htmlFor="checkout-city">City</label>
                    <input
                      id="checkout-city"
                      name="city"
                      value={formData.city}
                      onChange={handleFieldChange}
                      autoComplete="address-level2"
                      aria-invalid={Boolean(errors.city)}
                    />
                    {errors.city && (
                      <p className="checkout-field__error">{errors.city}</p>
                    )}
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="checkout-region">State</label>
                    <input
                      id="checkout-region"
                      name="region"
                      value={formData.region}
                      onChange={handleFieldChange}
                      autoComplete="address-level1"
                      aria-invalid={Boolean(errors.region)}
                    />
                    {errors.region && (
                      <p className="checkout-field__error">{errors.region}</p>
                    )}
                  </div>
                  <div className="checkout-field">
                    <label htmlFor="checkout-postalCode">ZIP code</label>
                    <input
                      id="checkout-postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={(event) =>
                        updateField(
                          "postalCode",
                          event.target.value.replace(/[^\d-]/g, "").slice(0, 10)
                        )
                      }
                      autoComplete="postal-code"
                      inputMode="numeric"
                      aria-invalid={Boolean(errors.postalCode)}
                    />
                    {errors.postalCode && (
                      <p className="checkout-field__error">{errors.postalCode}</p>
                    )}
                  </div>
                </div>

                <div className="checkout-field">
                  <label htmlFor="checkout-phone">
                    Phone <span>(optional)</span>
                  </label>
                  <input
                    id="checkout-phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleFieldChange}
                    autoComplete="tel"
                    placeholder="For delivery questions"
                  />
                </div>
              </section>

              <section className="checkout-form__section" aria-labelledby="shipping-heading">
                <div className="checkout-form__section-heading">
                  <span>03</span>
                  <div>
                    <h2 id="shipping-heading">Shipping</h2>
                    <p>Tracked delivery to all 50 US states.</p>
                  </div>
                </div>
                <label className="checkout-shipping-method">
                  <input
                    id="checkout-shippingMethod"
                    type="radio"
                    name="shippingMethod"
                    value="standard"
                    checked={formData.shippingMethod === "standard"}
                    onChange={handleFieldChange}
                  />
                  <span className="checkout-shipping-method__dot" aria-hidden="true" />
                  <span>
                    <strong>Standard tracked delivery</strong>
                    <small>5–10 business days</small>
                  </span>
                  <strong>{shipping === 0 ? "Free" : formatMoney(shipping)}</strong>
                </label>
                {errors.shippingMethod && (
                  <p className="checkout-field__error">{errors.shippingMethod}</p>
                )}
              </section>

              <section className="checkout-form__section" aria-labelledby="payment-heading">
                <div className="checkout-form__section-heading">
                  <span>04</span>
                  <div>
                    <h2 id="payment-heading">Payment</h2>
                    <p>All transactions are encrypted and secure.</p>
                  </div>
                </div>

                <div className="checkout-payment">
                  <div className="checkout-payment__header">
                    <span>Credit card</span>
                    <div aria-label="Accepted cards">
                      <span>VISA</span>
                      <span>MC</span>
                      <span>AMEX</span>
                    </div>
                  </div>
                  <div className="checkout-payment__body">
                    <div className="checkout-field">
                      <label htmlFor="checkout-cardNumber">Card number</label>
                      <input
                        id="checkout-cardNumber"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={(event) =>
                          updateField("cardNumber", formatCardNumber(event.target.value))
                        }
                        autoComplete="cc-number"
                        inputMode="numeric"
                        placeholder="1234 5678 9012 3456"
                        aria-invalid={Boolean(errors.cardNumber)}
                      />
                      {errors.cardNumber && (
                        <p className="checkout-field__error">{errors.cardNumber}</p>
                      )}
                    </div>

                    <div className="checkout-form__row">
                      <div className="checkout-field">
                        <label htmlFor="checkout-expiry">Expiration date</label>
                        <input
                          id="checkout-expiry"
                          name="expiry"
                          value={formData.expiry}
                          onChange={(event) =>
                            updateField("expiry", formatExpiry(event.target.value))
                          }
                          autoComplete="cc-exp"
                          inputMode="numeric"
                          placeholder="MM / YY"
                          aria-invalid={Boolean(errors.expiry)}
                        />
                        {errors.expiry && (
                          <p className="checkout-field__error">{errors.expiry}</p>
                        )}
                      </div>
                      <div className="checkout-field">
                        <label htmlFor="checkout-cvv">Security code</label>
                        <input
                          id="checkout-cvv"
                          name="cvv"
                          value={formData.cvv}
                          onChange={(event) =>
                            updateField(
                              "cvv",
                              event.target.value.replace(/\D/g, "").slice(0, 4)
                            )
                          }
                          autoComplete="cc-csc"
                          inputMode="numeric"
                          placeholder="CVV"
                          aria-invalid={Boolean(errors.cvv)}
                        />
                        {errors.cvv && (
                          <p className="checkout-field__error">{errors.cvv}</p>
                        )}
                      </div>
                    </div>

                    <div className="checkout-field">
                      <label htmlFor="checkout-cardName">Name on card</label>
                      <input
                        id="checkout-cardName"
                        name="cardName"
                        value={formData.cardName}
                        onChange={handleFieldChange}
                        autoComplete="cc-name"
                        aria-invalid={Boolean(errors.cardName)}
                      />
                      {errors.cardName && (
                        <p className="checkout-field__error">{errors.cardName}</p>
                      )}
                    </div>
                  </div>
                </div>

                <label className="checkout-form__consent">
                  <input
                    id="checkout-acceptTerms"
                    name="acceptTerms"
                    type="checkbox"
                    checked={formData.acceptTerms}
                    onChange={handleFieldChange}
                    aria-invalid={Boolean(errors.acceptTerms)}
                  />
                  <span aria-hidden="true" />
                  <small>
                    I agree to the <Link to="/terms">terms and conditions</Link>{" "}
                    and acknowledge the <Link to="/privacy">privacy policy</Link>.
                  </small>
                </label>
                {errors.acceptTerms && (
                  <p className="checkout-field__error">{errors.acceptTerms}</p>
                )}
              </section>

              {submitMessage && (
                <p className="checkout-form__submit-error" role="alert">
                  {submitMessage}
                </p>
              )}

              <button
                className="checkout-form__pay-button"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing payment…" : `Pay ${formatMoney(total)}`}
              </button>
              <p className="checkout-form__secure-note">
                Secure checkout · 30-day returns · No payment data is stored
              </p>
            </form>
          </div>
        </div>

        <aside
          className="checkout-page__summary-column"
          aria-labelledby="checkout-summary-desktop"
        >
          <div className="checkout-page__summary-inner">
            {renderOrderSummary("desktop")}
          </div>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
